/// <amd-module name="Feed/Legacy/Protocols/PyPi/PyPiOverviewContentPanel" />
import * as React from "react";

import { List } from "OfficeFabric/List";

import * as Utils_String from "VSS/Core/Util/String";

import { Link } from "VSSUI/Link";

import { CiConstants } from "Feed/Common/Constants/Constants";
import { PackageFile } from "Feed/Common/Contracts/Feed";
import * as Resources from "Feed/Common/Resources";
import { ConnectToFeedButton } from "Feed/Legacy/Components/ConnectToFeedButton";
import { IPackageDetailsProps } from "Feed/Legacy/Protocols/Common/IPackageDetailsProps";
import { IPackageProtocol } from "Feed/Legacy/Protocols/Common/IPackageProtocol";
import { CopyableTextField, ICopyableTextFieldTelemetry } from "Feed/Legacy/Protocols/Components/CopyableTextField";
import { MarkdownRendererWrapper } from "Feed/Legacy/Protocols/Components/MarkdownRendererWrapper";
import { PackageTile } from "Feed/Legacy/Protocols/Components/PackageTile";
import { IPyPiProtocolMetadata } from "Feed/Legacy/Protocols/PyPi/IPyPiProtocolMetadata";
import { IPyPiRestApiUrlGeneratorService } from "Feed/Legacy/Protocols/PyPi/PyPiRestApiUrlGeneratorService";
import { HubAction } from "Feed/Legacy/Types/IHubState";

export class PyPiOverviewContentPanel extends React.PureComponent<IPackageDetailsProps> {
    public render(): JSX.Element {
        const { packageVersion } = this.props;
        const metadata = packageVersion.protocolMetadata;
        const pyPiMetadata = (metadata && metadata.data) as IPyPiProtocolMetadata;
        const isActive = !packageVersion.isDeleted;

        return (
            <div className="package-overview-content-panel">
                {isActive && this.renderGetPackageComponent()}

                {packageVersion.description && (
                    <PackageTile title={Resources.Summary} cssClass="package-description">
                        {packageVersion.description}
                    </PackageTile>
                )}

                {isActive && this.renderFilesComponent(packageVersion.files)}

                {this.renderDescription(pyPiMetadata.description, pyPiMetadata.descriptionContentType)}
            </div>
        );
    }

    private renderFilesComponent(files: PackageFile[]): JSX.Element {
        if (files && files.length) {
            return (
                <PackageTile title={Resources.Files}>
                    <List items={files.map(f => f.name)} onRenderCell={this.renderFile} />
                </PackageTile>
            );
        }
        return null;
    }

    private renderFile = (filename: string): JSX.Element => {
        return <Link {...this.linkProps}>{filename}</Link>;
    };

    private onFileClicked = async (event: React.MouseEvent<HTMLAnchorElement>) => {
        const anchorElement = event && (event.target as HTMLAnchorElement);
        const filename = anchorElement && anchorElement.text;

        if (filename) {
            const { feed, packageSummary, packageVersion, protocol } = this.props;
            const url = this.props.pageContext
                .getService<IPyPiRestApiUrlGeneratorService>("IPyPiRestApiUrlGeneratorService")
                .getFileDownloadUrl(feed, packageSummary.normalizedName, packageVersion.normalizedVersion, filename);

            const newWindow = window.open(url, "_blank");
            if (newWindow) {
                newWindow.opener = undefined;
            }
        }
    };

    private renderDescription(description: string, contentType: string): JSX.Element {
        if (description) {
            const isMarkdown = contentType && Utils_String.equals(contentType, "text/markdown", true);
            const cssClass = "package-description";

            if (isMarkdown) {
                return (
                    <PackageTile title={Resources.Description} cssClass={cssClass}>
                        <MarkdownRendererWrapper
                            pageContext={this.props.pageContext}
                            rawMarkdownContent={description}
                        />
                    </PackageTile>
                );
            }

            return (
                <PackageTile title={Resources.Description} cssClass={cssClass}>
                    {description}
                </PackageTile>
            );
        }

        return null;
    }

    private renderGetPackageComponent(): JSX.Element {
        const { feed, feedViews, packageSummary, packageVersion, protocol } = this.props;
        const textfieldId = "pypi-overview-textfield-label1";

        const command = protocol.getCopyInstallCommand(feed, packageSummary.name, packageVersion);
        const protocolMap: { [key: string]: IPackageProtocol } = {};
        protocolMap[protocol.key] = protocol;

        return (
            <PackageTile title={Resources.GetThisPackage} id={textfieldId}>
                <div className="get-package-tile">
                    <ConnectToFeedButton
                        pageContext={this.props.pageContext}
                        feed={feed}
                        feedViews={feedViews}
                        protocolMap={protocolMap}
                        hubAction={HubAction.Package}
                    />

                    <span className="install-instructions-then">{Resources.InstallInstructions_then}</span>

                    <CopyableTextField
                        pageContext={this.props.pageContext}
                        displayText={command}
                        text={command}
                        buttonAriaLabel={Resources.AriaLabel_CopyToClipBoard_Install_Copy}
                        textFieldAriaLabel={Resources.AriaLabel_CopyToClipBoard_Install}
                        telemetryProperties={
                            {
                                commandName: CiConstants.CopyCommandInstall,
                                feedName: feed.name,
                                packageName: packageSummary.name,
                                packageVersion: packageVersion.version,
                                protocol: protocol.name
                            } as ICopyableTextFieldTelemetry
                        }
                        ariaDescribedByIdForLabelText={textfieldId}
                    />
                </div>
            </PackageTile>
        );
    }

    private readonly linkProps = {
        href: "#",
        className: "package-link-text",
        rel: "noopener noreferrer",
        onClick: this.onFileClicked
    };
}
